/**
 * <PolicyConflictCard /> — surfaces a conflict between a customer
 * policy (extracted from a PDF) and the KSA Labor Law statutory
 * minimum (M27 PR 4).
 */

import { AlertTriangle, Check, Clock, FileWarning } from "lucide-react";
import { useTranslation } from "react-i18next";

export type ConflictDecision =
  | "use_statutory"
  | "use_override"
  | "decide_later";

export interface PolicyConflictCardProps {
  label: string;
  customerValue: string;
  statutoryValue: string;
  articleRef?: string;
  overrideAllowed: boolean;
  decision?: ConflictDecision | null;
  onDecide: (decision: ConflictDecision) => void;
}

export function PolicyConflictCard({
  label,
  customerValue,
  statutoryValue,
  articleRef,
  overrideAllowed,
  decision = null,
  onDecide,
}: PolicyConflictCardProps) {
  const { t } = useTranslation();
  return (
    <article
      className="rounded-xl border border-amber-200 bg-amber-50/60 p-4"
      role="region"
      aria-label={t("app.conflict_aria", { label })}
    >
      <header className="flex items-start gap-2">
        <AlertTriangle
          size={16}
          className="mt-0.5 shrink-0 text-amber-700"
          aria-hidden
        />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-900">
            {t("app.conflict_title")}
          </h3>
          <p className="mt-0.5 text-xs text-amber-800">{label}</p>
        </div>
      </header>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-text-tertiary">
            {t("app.conflict_your_pdf")}
          </p>
          <p className="mt-1 font-mono text-sm font-semibold text-text-primary">
            {customerValue}
          </p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-text-tertiary">
            {t("app.conflict_statutory_floor")}
            {articleRef && (
              <span className="ms-1 text-text-secondary">
                ({articleRef})
              </span>
            )}
          </p>
          <p className="mt-1 font-mono text-sm font-semibold text-text-primary">
            {statutoryValue}
          </p>
        </div>
      </div>

      {!overrideAllowed && (
        <p className="mt-2 inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          <FileWarning size={12} aria-hidden /> {t("app.conflict_override_rejected")}
        </p>
      )}

      <footer className="mt-3 flex flex-wrap items-center gap-2">
        <DecisionButton
          label={t("app.conflict_use_statutory")}
          hint={t("app.conflict_compliant")}
          icon={Check}
          selected={decision === "use_statutory"}
          onClick={() => onDecide("use_statutory")}
        />
        <DecisionButton
          label={t("app.conflict_use_override")}
          hint={
            overrideAllowed
              ? t("app.conflict_override_allowed_hint")
              : t("app.conflict_override_disabled_hint")
          }
          icon={Check}
          disabled={!overrideAllowed}
          selected={decision === "use_override"}
          onClick={() => onDecide("use_override")}
        />
        <DecisionButton
          label={t("app.conflict_decide_later")}
          hint={t("app.conflict_decide_later_hint")}
          icon={Clock}
          selected={decision === "decide_later"}
          onClick={() => onDecide("decide_later")}
        />
      </footer>
    </article>
  );
}

function DecisionButton({
  label,
  hint,
  icon: Icon,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  hint: string;
  icon: typeof Check;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      title={hint}
      className={[
        "inline-flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-start text-xs transition-colors",
        selected
          ? "border-brand bg-brand text-brand-fg"
          : disabled
            ? "border-border-subtle bg-surface-sunken text-text-tertiary opacity-60"
            : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-sunken",
      ].join(" ")}
    >
      <Icon size={12} aria-hidden className="mt-0.5 shrink-0" />
      <span>
        <span className="block font-medium">{label}</span>
        <span
          className={[
            "block text-xs",
            selected ? "text-brand-fg/80" : "text-text-tertiary",
          ].join(" ")}
        >
          {hint}
        </span>
      </span>
    </button>
  );
}
